/**
 * Authentication Service
 * Handles login, code verification, and token management
 */
import Axios from 'axios';
import { REACT_APP_URL_V2_OAUTH_LOGIN, REACT_APP_URL_V2_OAUTH_VALIDATE } from '../components/common/Routes/URLs';
import { applyDebugIp } from '../utils/helpers/debugIpOverride';
import { buildOAuthFormHeaders } from '../utils/helpers/oauthHeaders';
import { startSession } from '../utils/helpers/sessionManager';
import { commitTokens, getAccessToken, getDeviceHeaders } from '../utils/helpers/sessionStore';
import { BACKEND_IDS, getBackendBaseUrl, getBackendCredentials, getBackendTypeForUrl } from './ApiConfig';

const getAuthCredentials = (backendId) => getBackendCredentials(backendId);

/**
 * Get authentication token using OAuth
 * @param {string} url - OAuth token endpoint
 * @param {object} data - Login credentials
 * @param {string} backendId - BACKEND_IDS.BACKEND_1 or BACKEND_2; URL may override
 * @returns {Promise} Axios response
 */
export const getAuthToken = async (url, data, backendId = BACKEND_IDS.BACKEND_1) => {
  const finalBackendId = getBackendTypeForUrl(url, backendId);
  const baseUrl = getBackendBaseUrl(finalBackendId);
  const credentials = getAuthCredentials(finalBackendId);

  if (finalBackendId === BACKEND_IDS.BACKEND_2 || url.includes('/v2/')) {
    // Convert data to URL-encoded format string
    const formDataString = Object.keys(data)
      .filter((key) => data[key] !== undefined && data[key] !== null)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(data[key]))}`)
      .join('&');

    return Axios.post(baseUrl + url, formDataString, {
      headers: buildOAuthFormHeaders(credentials)
    });
  }

  // For v1 backend, use form-data (legacy)
  const FormData = require('form-data');
  const bodyFormData = new FormData();
  bodyFormData.set('username', data.username);
  bodyFormData.set('password', data.password);
  bodyFormData.set('grant_type', data.grant_type || 'password');
  if (data.token) {
    bodyFormData.set('token', data.token);
  }

  return Axios.post(baseUrl + url, bodyFormData, {
    headers: {
      ...getDeviceHeaders(),
      Authorization: 'Basic ' + btoa(credentials.user + ':' + credentials.password)
    }
  });
};

/**
 * Validate 6-digit code (without orgId) - just checks if code is valid
 * @param {string} code - 6-digit verification code
 * @param {string} backendVersion - Backend version (e.g., 'v1', 'v2')
 * @returns {Promise} Response indicating if code is valid
 */
export const validateCode = async (code, backendId = BACKEND_IDS.BACKEND_2) => {
  const baseUrl = getBackendBaseUrl(backendId);
  const response = await Axios.post(
    `${baseUrl}/api/auth/validate-code`,
    {
      code
    },
    {
      headers: {
        ...getDeviceHeaders(),
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

/**
 * Verify 6-digit code and get tokens
 * @param {string} code - 6-digit verification code
 * @param {string} orgId - Organization ID
 * @param {string} backendVersion - Backend version (e.g., 'v1', 'v2')
 * @returns {Promise} Response with access_token and refresh_token
 */
export const verifyCodeAndGetTokens = async (code, orgId, backendId = BACKEND_IDS.BACKEND_2) => {
  const baseUrl = getBackendBaseUrl(backendId);
  const response = await Axios.post(
    `${baseUrl}/api/auth/verify-code`,
    {
      code,
      orgId
    },
    {
      headers: {
        ...getDeviceHeaders(),
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

/**
 * Validate OTP with organization ID (Backend v2)
 * This is the new flow: user enters OTP, selects org, then validates
 * @param {string|null} otp - 6-digit OTP code sent to email (or 8, 10, etc.) or null if session is active
 * @param {string|number} orgId - Selected organization ID
 * @param {string} backendVersion - Backend version (defaults to V2 for v2 endpoints)
 * @param {string} [accessToken] - Pre-login token to authorize with; defaults to the one in storage
 * @returns {Promise} Response with access_token and refresh_token if valid
 */
export const validateOtpWithOrg = async (otp, orgId, backendId = BACKEND_IDS.BACKEND_2, accessToken = getAccessToken()) => {
  const baseUrl = getBackendBaseUrl(backendId);

  // Ensure orgId is a number
  const orgIdNumber = typeof orgId === 'string' ? parseInt(orgId, 10) : orgId;

  // Request body: orgId first, then otp (as per API specification)
  // If otp is null, send null (for active sessions)
  const requestBody = {
    orgId: orgIdNumber,
    otp: otp !== null && otp !== undefined ? String(otp) : null
  };

  // Prepare headers with token if available
  const headers = {
    ...getDeviceHeaders(),
    'Content-Type': 'application/json'
  };

  // Add Authorization header with Bearer token if token exists
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await Axios.post(`${baseUrl}${REACT_APP_URL_V2_OAUTH_VALIDATE}`, requestBody, {
    headers: applyDebugIp(headers)
  });
  return response.data;
};

/**
 * Get login data with menu for the selected organization.
 * The backend resolves the user identity from the JWT, so only the device and the
 * organization are sent. The response carries the resolved user (including lastLogin),
 * the role name and the menu for that organization.
 * @param {string} device - Device type (default: 'WEB')
 * @param {string|number} orgId - Selected organization ID
 * @param {string} backendId - defaults to BACKEND_2 for /api/v2/oauth/login
 * @returns {Promise} Response with user, roleName and menu
 * @author armandoricosandoval -ai-
 */
export const getLoginData = async (device = 'WEB', orgId = null, backendId = BACKEND_IDS.BACKEND_2) => {
  const baseUrl = getBackendBaseUrl(backendId);
  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('Access token is required to get login data');
  }

  const orgIdNumber = typeof orgId === 'string' ? parseInt(orgId, 10) : orgId;

  const response = await Axios.post(
    `${baseUrl}${REACT_APP_URL_V2_OAUTH_LOGIN}`,
    { device, orgId: orgIdNumber },
    {
      headers: {
        ...getDeviceHeaders(),
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      }
    }
  );

  return response.data;
};

/**
 * Stores the tokens of the session just opened and starts its refresh schedule.
 * @param {string} accessToken - Access token
 * @param {string} [refreshToken] - Refresh token; the current one is kept when omitted
 */
export const storeTokens = (accessToken, refreshToken = null) => {
  if (!accessToken) return;

  commitTokens({ accessToken, refreshToken: refreshToken === accessToken ? null : refreshToken });
  startSession();
};

export default {
  getAuthToken,
  validateCode,
  verifyCodeAndGetTokens,
  validateOtpWithOrg,
  getLoginData,
  storeTokens
};
